/**
 * Image Optimization Utilities
 * Provides lazy loading and responsive image handling
 */

interface ImageOptions {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  loading?: 'lazy' | 'eager';
  className?: string;
}

/**
 * Generate optimized image props with lazy loading
 */
export const getOptimizedImageProps = ({
  src,
  alt,
  width,
  height,
  loading = 'lazy',
  className = ''
}: ImageOptions) => {
  return {
    src,
    alt,
    width,
    height,
    loading,
    decoding: 'async' as const,
    className: `${className} transition-opacity duration-300`,
    onLoad: (e: React.SyntheticEvent<HTMLImageElement>) => {
      e.currentTarget.style.opacity = '1';
    },
    style: { opacity: 0 }
  };
};

/**
 * Preload critical images
 */
export const preloadImage = (src: string): void => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = src;
  document.head.appendChild(link);
};

/**
 * Lazy load images using Intersection Observer
 */
export const lazyLoadImages = (): void => {
  if ('IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          const src = img.dataset.src;
          if (src) {
            img.src = src;
            img.removeAttribute('data-src');
            observer.unobserve(img);
          }
        }
      });
    });

    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img);
    });
  }
};

/**
 * Convert image URL to WebP format if supported
 */
export const getWebPUrl = (url: string): string => {
  // Check if browser supports WebP
  const supportsWebP = document.createElement('canvas')
    .toDataURL('image/webp')
    .indexOf('data:image/webp') === 0;

  if (supportsWebP && url.includes('static.readdy.ai')) {
    // Add WebP format parameter if the CDN supports it
    return url.includes('?') ? `${url}&format=webp` : `${url}?format=webp`;
  }

  return url;
};

/**
 * Generate responsive image srcset
 */
export const generateSrcSet = (baseUrl: string, sizes: number[]): string => {
  return sizes
    .map(size => {
      const url = baseUrl.includes('?')
        ? `${baseUrl}&w=${size}`
        : `${baseUrl}?w=${size}`;
      return `${url} ${size}w`;
    })
    .join(', ');
};
